import type { FC } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { RiFlag2Line as FlagIcon } from 'react-icons/ri';
import { Stack, Typography } from '@mui/material';

import { OrgUnitSelectionStatus } from 'src/constants/orgUnit';
import type { ModuleOrPlanActionOrgUnit } from 'src/types/orgUnit';
import useSurveySettings from 'src/hooks/useSurveySettings';

import { styles } from './index.styles';

export interface OrgUnitLabelProps {
  orgUnit: ModuleOrPlanActionOrgUnit;
  showPriority?: boolean;
}

const OrgUnitLabel: FC<OrgUnitLabelProps> = ({ orgUnit, showPriority }) => {
  const { t } = useTranslation('common');
  const { minRespondentAmount } = useSurveySettings();

  // Temporary workaround because we have mixed snake_case and camelCase endpoint responses
  // TODO: unify the API responses to use a single format #1406
  const needsNegSelTooltip =
    ('selection_status' in orgUnit &&
      (orgUnit.selection_status ===
        OrgUnitSelectionStatus.LESS_THAN_MIN_RESPONDENT_AMOUNT ||
        orgUnit.selection_status ===
          OrgUnitSelectionStatus.NEGATIVE_SELECTION)) ||
    ('selectionStatus' in orgUnit &&
      (orgUnit.selectionStatus ===
        OrgUnitSelectionStatus.LESS_THAN_MIN_RESPONDENT_AMOUNT ||
        orgUnit.selectionStatus === OrgUnitSelectionStatus.NEGATIVE_SELECTION));
  const negselTooltip = needsNegSelTooltip
    ? ` -- ${t('orgUnitNegselTooltip', { minRespondentAmount })}`
    : '';

  const hasPriority = showPriority && 'priority' in orgUnit && orgUnit.priority;

  // Temporary workaround because we have mixed snake_case and camelCase endpoint responses
  // TODO: unify the API responses to use a single format #1406
  const orgUnitId =
    'orgUnitId' in orgUnit
      ? orgUnit.orgUnitId
      : 'org_unit_id' in orgUnit
        ? orgUnit.org_unit_id
        : '';

  return (
    <Stack
      direction="column"
      title={`${orgUnit.name} -- ${orgUnit.org_unit_id}${negselTooltip}`}
      sx={styles.label}
    >
      <Typography
        variant="h6"
        component="span"
        sx={[
          styles.name,
          ...(negselTooltip ? [styles.negsel] : []),
          ...(hasPriority ? [styles.priority] : []),
        ]}
      >
        {hasPriority && <FlagIcon size="16px" />}
        {orgUnit.name}
      </Typography>
      <Typography
        variant="h6"
        component="span"
        sx={[styles.id, ...(negselTooltip ? [styles.negsel] : [])]}
      >
        {orgUnitId as string | undefined}
      </Typography>
    </Stack>
  );
};

export default OrgUnitLabel;
